import { FAVORITE_DELIVERY_TYPE } from 'common/constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHandleEscPress } from 'common/hooks';
import { DeletableFavoriteAddressType } from 'types/favorite-address';
import { AddressSheetItemType } from '../utils';

type MenuCoords = {
  top: number;
  left: number;
};

type AddressAction = 'edit' | 'delete';

const ADDRESS_ACTIONS_MENU_TOP_OFFSET = 4;

type UseAddressActionsMenuOptions = {
  isContainerOpen: boolean;
  onActionSelect?: (action: AddressAction, addressType: DeletableFavoriteAddressType) => void;
};

export function useAddressActionsMenu({ isContainerOpen, onActionSelect }: UseAddressActionsMenuOptions) {
  const [openMenuId, setOpenMenuId] = useState<AddressSheetItemType | null>(null);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLElement | null>(null);

  const closeAddressMenu = useCallback(() => {
    setOpenMenuId(null);
    setMenuCoords(null);
    anchorRef.current = null;
  }, []);

  const isMenuOpen = openMenuId !== null && menuCoords !== null;

  useEffect(() => {
    if (!isContainerOpen) {
      closeAddressMenu();
    }
  }, [isContainerOpen, closeAddressMenu]);

  useEffect(() => {
    if (!openMenuId) {
      return;
    }

    const handleScroll = () => closeAddressMenu();

    window.addEventListener('scroll', handleScroll, true);

    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [openMenuId, closeAddressMenu]);

  useEffect(() => {
    if (!openMenuId) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;

      if (menuRef.current?.contains(target) || anchorRef.current?.contains(target)) {
        return;
      }

      closeAddressMenu();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);

    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [openMenuId, closeAddressMenu]);

  useHandleEscPress(closeAddressMenu, isMenuOpen);

  const handleAddressMenuOpen = useCallback(
    (id: AddressSheetItemType, anchor: HTMLElement) => {
      if (openMenuId === id) {
        closeAddressMenu();
        return;
      }

      const rect = anchor.getBoundingClientRect();

      anchorRef.current = anchor;
      setMenuCoords({
        top: rect.bottom + ADDRESS_ACTIONS_MENU_TOP_OFFSET,
        left: rect.right,
      });
      setOpenMenuId(id);
    },
    [closeAddressMenu, openMenuId],
  );

  const handleAddressActionSelect = useCallback(
    (value: AddressAction) => {
      if (openMenuId && openMenuId !== FAVORITE_DELIVERY_TYPE.REGION && onActionSelect) {
        onActionSelect(value, openMenuId);
      }

      closeAddressMenu();
    },
    [closeAddressMenu, onActionSelect, openMenuId],
  );

  return {
    openMenuId,
    menuCoords,
    menuRef,
    isMenuOpen,
    handleAddressMenuOpen,
    handleAddressActionSelect,
    closeAddressMenu,
  };
}
